// Which Keycloak realm this browser signs in against (fsgadmin/fsgnext#2
// U14/U21, §8).
//
// fsgnext has TWO realms and the portal serves both:
//
//   community  — every account holder (the ~87k legacy TYPO3 population after
//                the claim funnel, plus sign-ups). Passkey-or-brokered, no
//                passwords (U22). Client id is ENV-SPECIFIC — `fsgnext-portal-dev`
//                carries the `access` role the dev verifier demands (U21), so
//                the two environments cannot share one client. THE DEFAULT.
//   fsg        — officials/staff, the realm go-usermanager has always fed.
//                Client `fsgnext-portal`, one id for both environments.
//
// The choice is a RUNTIME one (a person picks "Staff sign-in" once) over a
// BUILD-TIME map (the client ids are baked per environment, like GRAPHQL_URL —
// there is no runtime config indirection in this SPA). It is remembered in
// localStorage so every later visit, token refresh and reconnect uses the same
// realm without asking again; keycloak-js holds the realm on the instance
// itself, so the refresh loop needs no further help.
//
// Everything here is pure over an explicit `RealmEnv` so it is testable — the
// `import.meta.env` read happens in exactly one place (realmEnv()).

/** The two realms, named by the audience rather than by the realm string. */
export type RealmChoice = 'community' | 'fsg'

/** Community is the default: it is the realm nearly every human belongs to. */
export const DEFAULT_REALM_CHOICE: RealmChoice = 'community'

/** localStorage — NOT sessionStorage. The realm is a property of the person
 *  using this browser, not of one tab's session; asking a staff member to
 *  re-pick "Staff sign-in" on every visit is exactly the friction the
 *  remembered choice exists to remove. */
const STORAGE_KEY = 'portal.realm'

/** A deep link may name the realm: `?realm=fsg` is the bookmarkable staff
 *  entry point, and it PERSISTS the choice so the query string is needed
 *  exactly once. */
const QUERY_KEY = 'realm'

/** The build-time half: the URL and, per realm, the realm string + client id. */
export interface RealmEnv {
  url: string
  communityRealm: string
  communityClient: string
  fsgRealm: string
  fsgClient: string
}

/** One realm's resolved keycloak-js constructor arguments, plus the label the
 *  UI shows for it. */
export interface RealmConfig {
  choice: RealmChoice
  label: string
  url: string
  realm: string
  clientId: string
}

/**
 * The build-time realm map, read from the compiled-in env exactly once.
 *
 * `KEYCLOAK_CLIENT_COMMUNITY` is the value CI varies per environment
 * (`fsgnext-portal-dev` / `fsgnext-portal-prod`); everything else is constant
 * across environments and only overridable for a local run.
 */
export function realmEnv(): RealmEnv {
  return {
    url: import.meta.env.KEYCLOAK_URL,
    communityRealm: import.meta.env.KEYCLOAK_REALM_COMMUNITY,
    communityClient: import.meta.env.KEYCLOAK_CLIENT_COMMUNITY,
    fsgRealm: import.meta.env.KEYCLOAK_REALM_FSG,
    fsgClient: import.meta.env.KEYCLOAK_CLIENT_FSG,
  }
}

/** Resolve one choice against the build-time map. */
export function resolveRealm(choice: RealmChoice, env: RealmEnv): RealmConfig {
  if (choice === 'fsg') {
    return {
      choice,
      label: 'Staff',
      url: env.url,
      realm: env.fsgRealm,
      clientId: env.fsgClient,
    }
  }
  return {
    choice,
    label: 'Community',
    url: env.url,
    realm: env.communityRealm,
    clientId: env.communityClient,
  }
}

/** Whether a realm is configured well enough to attempt a sign-in. A missing
 *  client id must fail CLOSED (src/boot/keycloak.ts renders the static error
 *  screen) rather than silently sign somebody into the wrong client. */
export function realmConfigured(cfg: RealmConfig): boolean {
  return Boolean(cfg.url && cfg.realm && cfg.clientId)
}

function isRealmChoice(value: unknown): value is RealmChoice {
  return value === 'community' || value === 'fsg'
}

/**
 * The remembered choice, or null when this browser has never picked one.
 *
 * A `?realm=` query parameter wins and is written through, so a staff bookmark
 * of `https://portal.example/?realm=fsg` both signs in against fsg AND makes
 * every later visit do the same. An unrecognised value is IGNORED rather than
 * silently degrading to the default (ui-conventions.md #6) — the caller sees
 * the same "no choice yet" it would for an absent parameter and renders the
 * chooser, which is the visible feedback that rule asks for.
 */
export function readRealmChoice(search = '', storage: Storage | null = safeStorage()): RealmChoice | null {
  const fromQuery = new URLSearchParams(search).get(QUERY_KEY)
  if (isRealmChoice(fromQuery)) {
    rememberRealmChoice(fromQuery, storage)
    return fromQuery
  }
  const stored = storage?.getItem(STORAGE_KEY) ?? null
  return isRealmChoice(stored) ? stored : null
}

/** Persist the choice for every later visit, refresh and reconnect. */
export function rememberRealmChoice(choice: RealmChoice, storage: Storage | null = safeStorage()): void {
  try {
    storage?.setItem(STORAGE_KEY, choice)
  } catch {
    // A browser with storage disabled still gets a working sign-in — it just
    // re-asks. Never let persistence failure break authentication.
  }
}

/** Forget the choice, so the next load asks again. Used when switching realms
 *  deliberately and when a sign-in against the remembered realm is abandoned. */
export function forgetRealmChoice(storage: Storage | null = safeStorage()): void {
  try {
    storage?.removeItem(STORAGE_KEY)
  } catch {
    // See rememberRealmChoice.
  }
}

/** localStorage, or null where it does not exist / is blocked (Safari private
 *  mode throws on ACCESS, not only on write). */
function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}
